/* FamilyPlansPanel — SuperAdmin / Admin view of every Family Plan.
 *
 * Shows owner + member counts + current expiry, lets admins extend or
 * shorten the expiry for support cases (comps, extensions, apologies).
 * Also exposes a "Run reminder sweep now" button that invokes the same
 * cron sweep for one-off manual fires.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Calendar, Bell, CheckCircle, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function toInputDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export default function FamilyPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [edits, setEdits] = useState({});
  const [reminderBusy, setReminderBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/family-plans");
      setPlans(data.plans || []);
      // Pre-fill edit map with the current expiry per plan.
      const seed = {};
      (data.plans || []).forEach((p) => { seed[p.plan_id] = toInputDate(p.expires_at); });
      setEdits(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Family Plans");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveExpiry = async (plan) => {
    const next = edits[plan.plan_id];
    if (!next) { toast.error("Pick a date first."); return; }
    if (!window.confirm(`Set ${plan.owner_email || plan.owner_name || "this plan"}'s Family Plan to expire on ${next}? This cascades the new expiry to every active member.`)) return;
    setBusyId(plan.plan_id);
    try {
      await api.patch(`/admin/family-plans/${plan.plan_id}`, { expires_at: next });
      toast.success("Expiry updated. Members' entitlement cascaded.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusyId(""); }
  };

  const runReminders = async () => {
    if (!window.confirm("Run the 30/14/3-day expiry reminder sweep now? Already-sent windows are skipped (idempotent).")) return;
    setReminderBusy(true);
    try {
      const { data } = await api.post("/admin/family-plans/run-reminders");
      toast.success(`Reminder sweep complete — ${data.sent || 0} email${data.sent === 1 ? "" : "s"} sent across ${data.windows_fired || 0} window${data.windows_fired === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setReminderBusy(false); }
  };

  return (
    <div data-testid="admin-family-plans-panel">
      <p className="overline mb-2">Support tools</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Family Plans</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">
        Every active Family Plan — owner, member count and expiry. Extensions cascade
        the new expiry to every member of that plan so no-one loses access mid-term.
      </p>

      <div className="card-recipe !p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-rf-ink-soft">
          <Bell size={18} weight="duotone" className="text-rf-orange" />
          Expiry reminders fire automatically each night (30 / 14 / 3 days before expiry).
        </div>
        <button
          type="button" onClick={runReminders} disabled={reminderBusy}
          data-testid="admin-family-plans-run-reminders"
          className="btn-amber !py-2 !px-4 !text-sm disabled:opacity-50"
        >
          {reminderBusy ? "Running sweep…" : "Run reminder sweep now"}
        </button>
      </div>

      {loading && <p className="text-rf-ink-soft italic">Loading…</p>}

      {!loading && plans.length === 0 && (
        <p className="text-rf-ink-soft italic" data-testid="admin-family-plans-empty">
          No Family Plans yet.
        </p>
      )}

      {!loading && plans.length > 0 && (
        <ul className="space-y-3" data-testid="admin-family-plans-list">
          {plans.map((p) => {
            const nextVal = edits[p.plan_id] || "";
            const currentIso = toInputDate(p.expires_at);
            const changed = nextVal && nextVal !== currentIso;
            return (
              <li
                key={p.plan_id}
                className="card-recipe !p-5 flex flex-wrap items-start justify-between gap-5"
                data-testid={`admin-family-plan-${p.plan_id}`}
              >
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Users size={18} weight="duotone" className="text-rf-orange" />
                    <p className="font-display font-black text-rf-ink text-base">
                      {p.owner_name || p.owner_email || "(unknown owner)"}
                    </p>
                    {p.active ? (
                      <span className="chip !bg-emerald-500/20 !text-emerald-800 inline-flex items-center gap-1">
                        <CheckCircle size={12} weight="bold" /> Active
                      </span>
                    ) : (
                      <span className="chip !bg-red-500/20 !text-red-800 inline-flex items-center gap-1">
                        <XCircle size={12} weight="bold" /> Expired
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-rf-ink-soft">{p.owner_email || ""}</p>
                  <p className="text-xs text-rf-ink-soft mt-2">
                    <strong>{p.joined_count || 0}</strong> joined · <strong>{p.pending_count || 0}</strong> pending · cap <strong>{p.max_members || 5}</strong>
                  </p>
                  <p className="text-xs text-rf-ink-soft mt-0.5">
                    Current expiry: <strong>{fmtDate(p.expires_at)}</strong>
                  </p>
                </div>

                <div className="flex items-end gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-rf-ink-soft block mb-1 flex items-center gap-1">
                      <Calendar size={12} /> New expiry
                    </label>
                    <input
                      type="date"
                      value={nextVal}
                      onChange={(e) => setEdits((m) => ({ ...m, [p.plan_id]: e.target.value }))}
                      data-testid={`admin-family-plan-expiry-input-${p.plan_id}`}
                      className="input-rf !py-1.5 !px-3 !text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!changed || busyId === p.plan_id}
                    onClick={() => saveExpiry(p)}
                    data-testid={`admin-family-plan-save-${p.plan_id}`}
                    className="btn-amber !py-2 !px-4 !text-sm disabled:opacity-40"
                  >
                    {busyId === p.plan_id ? "Saving…" : "Save"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
